import React from 'react';

import { Button as HeadlessButton } from '@headlessui/react';

import cn from '~/utils/cn';

export type IconButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning';

export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps {
  children: React.ReactNode;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  id?: string;
  title?: string;
  name?: string;
  value?: string;
  form?: string;
  ariaLabel?: string;
  ariaDescribedby?: string;
  round?: boolean;
}

const IconButton: React.FC<IconButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  onClick,
  className = '',
  type = 'button',
  id,
  title,
  name,
  value,
  form,
  ariaLabel,
  ariaDescribedby,
  round = false,
}) => {
  // Variant styles for icon buttons - transparent background with colored text/icon
  const variantStyles: Record<IconButtonVariant, string> = {
    primary: 'bg-transparent text-blue-1000 hover:bg-blue-100',
    secondary: 'bg-transparent text-grey-700 hover:bg-grey-100',
    success: 'bg-transparent text-green-700 hover:bg-green-100',
    danger: 'bg-transparent text-red-700 hover:bg-red-100',
    warning: 'bg-transparent text-yellow-700 hover:bg-yellow-100',
  };

  // Size styles for icon buttons - equal width and height for square/round buttons
  const sizes: Record<IconButtonSize, string> = {
    sm: 'p-2 text-sm w-6 h-6',
    md: 'p-4 text-base w-10 h-10',
    lg: 'p-8 text-lg w-14 h-14',
  };

  const baseStyles = 'flex items-center justify-center transition-colors cursor-pointer focus:outline-none';

  const shapeStyles = round ? 'rounded-full' : 'rounded-md';

  const disabledStyles = disabled ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <HeadlessButton
      as="button"
      id={id}
      name={name}
      value={value}
      form={form}
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(baseStyles, variantStyles[variant], sizes[size], shapeStyles, disabledStyles, className)}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
      title={title}
    >
      {children}
    </HeadlessButton>
  );
};

export default IconButton;
